import { Injectable } from '@angular/core';
import { Storage, ref, uploadBytes, getDownloadURL, deleteObject } from '@angular/fire/storage';

@Injectable({
  providedIn: 'root'
})
export class PhotoService {
  constructor(private storage: Storage) {}

  private async compressImage(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const maxWidth = 400; // Max width for the image
      const maxHeight = 400; // Max height for the image
      const quality = 0.7; // Image quality (0.7 = 70% quality)
      
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event: any) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Could not create blob'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  async deletePhoto(url: string) {
    try {
      // Convert the URL back to a storage path
      const storageRef = ref(this.storage, this.getPathFromUrl(url));
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Error deleting photo:', error);
      throw error;
    }
  }

  private getPathFromUrl(url: string): string {
    // Extract the path from the Firebase Storage URL
    // URL format: https://firebasestorage.googleapis.com/v0/b/[project]/o/[path]?...
    const matches = url.match(/\/o\/(.+?)\?/);
    if (!matches) throw new Error('Invalid storage URL');
    return decodeURIComponent(matches[1]);
  }

  async uploadPhoto(file: File, path: string): Promise<string> {
    try {
      // Compress the image before uploading
      const compressedBlob = await this.compressImage(file);
      const compressedFile = new File([compressedBlob], file.name, {
        type: 'image/jpeg'
      });

      const storageRef = ref(this.storage, path);
      const snapshot = await uploadBytes(storageRef, compressedFile);
      return await getDownloadURL(snapshot.ref);
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  async uploadPhotoFromDataUrl(dataUrl: string, path: string): Promise<string> {
    try {
      // Convert base64 to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
      return await this.uploadPhoto(file, path);
    } catch (error) {
      console.error('Error uploading from data URL:', error);
      throw error;
    }
  }
} 